import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import WhatsAppFloat from './components/layout/WhatsAppFloat';
import HomePage from './pages/HomePage';
import { DestinationsPage, DestinationDetailPage } from './pages/DestinationsPages';
import { PackagesPage, PackageDetailPage } from './pages/PackagesPages';
import { AboutPage, ContactPage, ThemesPage } from './pages/StaticPages';
import { HowItWorksPage, HolidayCategoryPage, BlogPage, BlogPostPage, DestinationWeddingPage } from './pages/ExtraPages';
import { InternationalDestinationsPage, InternationalDestinationDetailPage } from './pages/InternationalPages';
import { HimachalToursPage, HimachalTourDetailPage } from './pages/HimachalPages';
import { DomesticDestinationsPage, DomesticDestinationDetailPage } from './pages/DomesticPages';
import { SubRegionPage } from './pages/SubRegionPage';
import SelfDrivePage from './pages/SelfDrivePage';
import KinnaurSpitiCabPage from './pages/KinnaurSpitiCabPage';
import TravelDiaries from './pages/TravelDiaries';
import ThankYouPage from './pages/ThankYouPage';
import EnquiryPopup from './components/layout/EnquiryPopup';

import { Toaster } from './components/ui/toaster';

function useHashScroll() {
  const { hash } = useLocation();
  const observerRef = useRef(null);

  useLayoutEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    if (!id) return;

    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'start' });
        return true;
      }
      return false;
    };

    if (scrollTo()) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new MutationObserver(() => {
      if (scrollTo() && observerRef.current) {
        observerRef.current.disconnect();
      }
    });

    observerRef.current.observe(document.body, { childList: true, subtree: true });

    const fallback = setTimeout(() => {
      scrollTo();
      if (observerRef.current) observerRef.current.disconnect();
    }, 3000);

    const onWindowLoad = () => { scrollTo(); if (observerRef.current) observerRef.current.disconnect(); };
    window.addEventListener('load', onWindowLoad);

    return () => {
      clearTimeout(fallback);
      window.removeEventListener('load', onWindowLoad);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hash]);
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
  }, []);
  return null;
}

function AppLayout() {
  const { pathname } = useLocation();
  useHashScroll();
  return (
    <>
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/destinations" element={<DestinationsPage />} />
          <Route path="/destinations/:slug" element={<DestinationDetailPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/:slug" element={<PackageDetailPage />} />
          <Route path="/holidays/destination-wedding" element={<DestinationWeddingPage />} />
          <Route path="/holidays/:slug" element={<HolidayCategoryPage />} />
          <Route path="/international" element={<InternationalDestinationsPage />} />
          <Route path="/international/:slug" element={<InternationalDestinationDetailPage />} />
          <Route path="/himachal-tours" element={<HimachalToursPage />} />
          <Route path="/himachal-tours/:slug" element={<HimachalTourDetailPage />} />
          <Route path="/domestic" element={<DomesticDestinationsPage />} />
          <Route path="/domestic/:slug" element={<DomesticDestinationDetailPage />} />
          <Route path="/explore/:slug" element={<SubRegionPage />} />
          <Route path="/self-drive" element={<SelfDrivePage />} />
          <Route path="/kinnaur-spiti-cab-connect" element={<KinnaurSpitiCabPage />} />
          <Route path="/travel-diaries" element={<TravelDiaries />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />

        </Routes>
      </main>
      <Footer />
      <WhatsAppFloat />
      {pathname !== '/thank-you' && <EnquiryPopup />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
